import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type {
  PageConfig,
  SectionConfig,
  SectionItemConfig,
  SettingsSchema,
} from './types/settings';
import {
  getAppHostname,
  getSettings,
  loadClientStylesheet,
  resolveAssetUrl,
} from './api/assets';
import { setApiBaseUrl } from './api/client';
import mainpage from './defaults/mainpage.json';

/**
 * Configuração da instância, resolvida pelo hostname.
 *
 * `src/defaults/mainpage.json` descreve TODAS as páginas do produto. O
 * `public/assets/<hostname>/settings.json` da marca só precisa declarar o que
 * quer sobrescrever; o merge acontece por `route` (páginas), por `name`
 * (seções) e por `name` (itens). Nunca remova o campo `name` de um item.
 *
 * Ver `docs/whitelabel/settings-schema.md`.
 */

const defaultSettings: SettingsSchema = mainpage as SettingsSchema;

function mergeSectionItems(
  defaultItems: SectionItemConfig[],
  overrideItems: SectionItemConfig[]
): SectionItemConfig[] {
  const merged = [...defaultItems];

  for (const override of overrideItems) {
    const index = merged.findIndex((item) => item.name === override.name);
    if (index >= 0) {
      merged[index] = { ...merged[index], ...override };
    } else {
      merged.push(override);
    }
  }

  return merged;
}

function mergeSections(
  defaultSections: SectionConfig[],
  overrideSections: SectionConfig[]
): SectionConfig[] {
  const merged = [...defaultSections];

  for (const override of overrideSections) {
    const index = merged.findIndex((section) => section.name === override.name);
    if (index >= 0) {
      merged[index] = {
        ...merged[index],
        ...override,
        items: mergeSectionItems(merged[index].items || [], override.items || []),
      };
    } else {
      merged.push(override);
    }
  }

  return merged;
}

function mergePages(defaultPages: PageConfig[], overridePages: PageConfig[]): PageConfig[] {
  const merged = [...defaultPages];

  for (const override of overridePages) {
    const index = merged.findIndex((page) => page.route === override.route);
    if (index >= 0) {
      merged[index] = {
        ...merged[index],
        ...override,
        sections: mergeSections(merged[index].sections || [], override.sections || []),
      };
    } else {
      merged.push(override);
    }
  }

  return merged;
}

export function mergeSettings(
  defaults: SettingsSchema,
  overrides: Partial<SettingsSchema>
): SettingsSchema {
  const merged: SettingsSchema = {
    ...defaults,
    ...overrides,
    brand: { ...(defaults.brand || {}), ...(overrides.brand || {}) },
    socials: { ...(defaults.socials || {}), ...(overrides.socials || {}) },
    appLinks: { ...(defaults.appLinks || {}), ...(overrides.appLinks || {}) },
    legalDocs: { ...(defaults.legalDocs || {}), ...(overrides.legalDocs || {}) },
  };

  if (defaults.pages && overrides.pages) {
    merged.pages = mergePages(defaults.pages, overrides.pages);
  }

  return merged;
}

interface SettingsContextValue {
  settings: SettingsSchema;
  loading: boolean;
  /** `null` quando a marca simplesmente não tem settings.json próprio. */
  error: string | null;
  /** Hostname usado para resolver assets e o header `x-caller-url`. */
  app: string;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: defaultSettings,
  loading: true,
  error: null,
  app: '',
});

export function useSettings(): SettingsContextValue {
  return useContext(SettingsContext);
}

interface SettingsProviderProps {
  children: React.ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<SettingsSchema>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const app = getAppHostname();

  useEffect(() => {
    let cancelled = false;

    async function init() {
      let merged = defaultSettings;

      try {
        const brandSettings = await getSettings(app);
        merged = mergeSettings(defaultSettings, brandSettings);
      } catch (err) {
        // Hostname sem pasta própria em public/assets/ é o caso normal: a marca
        // não precisa de override visual, e nome, logo, cor e SAC vêm do ERP em
        // runtime. Só registramos para depuração.
        console.info(`Sem settings.json para ${app}; usando os defaults.`);
      }

      if (cancelled) return;

      // A base do middleware precisa valer ANTES de qualquer chamada. O
      // BrandProvider e o AppRoutes esperam `loading` virar false, que só
      // acontece no fim deste efeito — então nenhuma requisição sai com a base
      // errada. É assim que um mesmo build atende HML e produção.
      setApiBaseUrl(merged.middlewareUrl);

      document.title = merged.pageTitle;

      if (merged.favIcon) {
        let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        // Caminho relativo resolve dentro de `/assets/<hostname>/`, que é o
        // contrato descrito em docs/whitelabel/settings-schema.md. Sem isto um
        // `"favIcon": "images/favicon.ico"` quebraria em toda rota que não a raiz.
        favicon.href = resolveAssetUrl(merged.favIcon, app);
      }

      await loadClientStylesheet(app);

      if (cancelled) return;

      setSettings(merged);
      setError(null);
      setLoading(false);
    }

    init().catch((err: unknown) => {
      if (cancelled) return;
      console.error('Falha ao inicializar as configurações:', err);
      setError(err instanceof Error ? err.message : 'Falha ao carregar configurações');
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [app]);

  const value = useMemo<SettingsContextValue>(
    () => ({ settings, loading, error, app }),
    [settings, loading, error, app]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}
